import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import { EmptyState } from "@/components/empty-state";
import { PayoutProofViewer } from "@/components/payout-proof-viewer";
import { PayoutDetailsForm } from "@/components/studio/payout-details-form";


import { Skeleton } from "@/components/ui/skeleton";
import { useI18n } from "@/lib/i18n";
import { formatMoney } from "@/lib/money";
import { getMyTips } from "@/lib/tips.functions";
import { getMyPayoutSummary } from "@/lib/payments/payouts.functions";

export const Route = createFileRoute("/_authenticated/studio/support")({
  head: () => ({
    meta: [
      { title: "Reader support — Creator Studio — Quibblies" },
      {
        name: "description",
        content:
          "Private record of the tips readers have sent for your Quibblies writing, including messages and anonymous support.",
      },
      { property: "og:title", content: "Reader support — Quibblies Creator Studio" },
      { property: "og:description", content: "Tips readers have sent for your writing." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: SupportPage,
});

function SupportPage() {
  const { language } = useI18n();
  const locale = language === "en" ? "en-GB" : language;
  const fetchTips = useServerFn(getMyTips);
  const { data, isLoading } = useQuery({ queryKey: ["studio-tips"], queryFn: () => fetchTips() });
  const fetchPayouts = useServerFn(getMyPayoutSummary);
  const payoutSummary = useQuery({
    queryKey: ["studio-payouts"],
    queryFn: () => fetchPayouts(),
  });

  if (isLoading) return <Skeleton className="h-48 w-full rounded-2xl" />;

  const rows = data ?? [];
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No support received yet"
        description="Turn on “Enable tipping” in your writer profile to show a Tip Writer button to readers. Contributions appear here, with the platform fee shown in full. This information is private to you."
      />
    );
  }

  const completed = rows.filter((row) => row.status === "completed");
  // Gross, fee and net are all stored on the tip by the database, so this page
  // reports exactly what was applied rather than recalculating it.
  const totals = completed.reduce<
    Record<string, { gross: number; fee: number; net: number }>
  >((acc, row) => {
    const bucket = acc[row.currency] ?? { gross: 0, fee: 0, net: 0 };
    bucket.gross += row.amount_minor;
    bucket.fee += row.platform_fee_minor;
    bucket.net += row.net_amount_minor;
    acc[row.currency] = bucket;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-border/60 bg-background/60 p-5">
        <h2 className="text-xs uppercase tracking-wide text-muted-foreground">
          Payout details
        </h2>
        <div className="mt-3">
          <PayoutDetailsForm />
        </div>
      </section>

      <section className="rounded-2xl border border-border/60 bg-background/60 p-5">

        <h2 className="text-xs uppercase tracking-wide text-muted-foreground">Your payouts</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Tips are collected by Quibblies and paid on to you. An admin approves your payout once
          you are owed at least {formatMoney(payoutSummary.data?.minimumMinor ?? 1000, "GBP", locale)}
          . After approval, funds usually take {payoutSummary.data?.arrivalWindow ?? "3–5 working days"}{" "}
          to reach your bank.
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {(payoutSummary.data?.owed ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing awaiting payout right now.</p>
          ) : (
            (payoutSummary.data?.owed ?? []).map((row) => (
              <div key={row.currency} className="rounded-xl border border-border/60 p-4 text-sm">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Awaiting payout
                </p>
                <p className="mt-1 font-display text-2xl text-primary-glow">
                  {formatMoney(row.outstandingMinor, row.currency, locale)}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {row.eligible
                    ? "Eligible — this will be approved in the next payout run."
                    : "Below the payout minimum, so it rolls over."}
                </p>
              </div>
            ))
          )}
        </div>
        {(payoutSummary.data?.payouts ?? []).length > 0 ? (
          <ul className="mt-4 space-y-2 text-sm">
            {(payoutSummary.data?.payouts ?? []).map((payout) => (
              <li
                key={payout.id}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-border/40 pb-2 last:border-0"
              >
                <span className="text-foreground">
                  {formatMoney(payout.amountMinor, payout.currency, locale)}
                  <span className="ml-2 text-xs text-muted-foreground">
                    {payout.state === "settled"
                      ? "Settled"
                      : `Approved — allow ${payoutSummary.data?.arrivalWindow ?? "3–5 working days"}`}
                  </span>
                </span>
                <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  {payout.method.replace(/_/g, " ")}
                  {payout.reference ? ` · ${payout.reference}` : ""} ·{" "}
                  {new Date(payout.paidAt).toLocaleDateString()}
                  {payout.proofUrl ? (
                    <PayoutProofViewer
                      url={payout.proofUrl}
                      name={payout.proofName}
                      caption={`Payment proof for your ${new Date(payout.paidAt).toLocaleDateString()} payout.`}
                    />
                  ) : null}

                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <section className="rounded-2xl border border-border/60 bg-background/60 p-5">
        <h2 className="text-xs uppercase tracking-wide text-muted-foreground">
          Completed support
        </h2>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          {Object.keys(totals).length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing completed yet.</p>
          ) : (
            Object.entries(totals).map(([currency, amount]) => (
              <dl key={currency} className="rounded-xl border border-border/60 p-4 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Readers tipped</dt>
                  <dd className="text-foreground">{formatMoney(amount.gross, currency, locale)}</dd>
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                  <dt>Quibblies platform fee</dt>
                  <dd>-{formatMoney(amount.fee, currency, locale)}</dd>
                </div>
                <div className="mt-2 flex items-center justify-between border-t border-border/60 pt-2">
                  <dt className="text-foreground">You received</dt>
                  <dd className="font-display text-2xl text-primary-glow">
                    {formatMoney(amount.net, currency, locale)}
                  </dd>
                </div>
              </dl>
            ))
          )}
        </div>
      </section>

      <ul className="space-y-3">
        {rows.map((row) => (
          <li key={row.id} className="rounded-2xl border border-border/60 bg-background/60 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-display text-lg text-foreground">
                {formatMoney(row.net_amount_minor, row.currency, locale)}
                <span className="ml-2 text-xs font-sans text-muted-foreground">
                  from {formatMoney(row.amount_minor, row.currency, locale)} · fee{" "}
                  {formatMoney(row.platform_fee_minor, row.currency, locale)}
                </span>
              </p>
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                {row.status}
              </span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {/* Anonymity is applied in the database — the identity never reaches this page. */}
              {row.anonymous
                ? "Anonymous reader"
                : (row.supporter_display_name ?? "A reader")}
              {row.publication_title ? ` · for ${row.publication_title}` : ""} ·{" "}
              {new Date(row.created_at).toLocaleDateString()}
            </p>
            {row.message ? (
              <p className="mt-2 text-sm text-muted-foreground">“{row.message}”</p>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
